import type React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SignatureCanvas from 'react-signature-canvas'
import { Country, State, City } from 'country-state-city'
import { useBulkSubmissions } from '../BulkSubmissionContext'
import { Dialog } from '@headlessui/react'
import { X } from 'lucide-react'
import PDFRenderer from 'components/shared/PDFRenderer.tsx'
import StepIndicator from './StepIndicator'

// Utility functions for location data
const getCountries = () => Country.getAllCountries()
const getStatesByCountry = (countryCode: string) =>
  State.getStatesOfCountry(countryCode)
const getCitiesByState = (countryCode: string, stateCode: string) =>
  City.getCitiesOfState(countryCode, stateCode)

// SignatureCanvas component
const SignatureCanvasComponent = ({ control }: { control: any }) => {
  const sigCanvas = useRef<any>(null)

  const clear = () => {
    sigCanvas.current?.clear()
  }

  return (
    <div className='w-full'>
      <div className='flex items-center justify-between mb-2'>
        <label className='block text-sm font-medium text-gray-700'>Signature</label>
        <button type='button' onClick={clear} className='button error small'>
          Clear Signature
        </button>
      </div>
      <Controller
        name='signature'
        control={control}
        defaultValue=''
        render={({ field }) => (
          <SignatureCanvas
            ref={sigCanvas}
            onEnd={() => field.onChange(sigCanvas.current?.toDataURL())}
            canvasProps={{
              className:
                'relative w-auto border border-secondary-purple/20 rounded-lg',
            }}
          />
        )}
      />
    </div>
  )
}

const SignAgreement = () => {
  const { updateStep } = useBulkSubmissions()
  const { register, control, handleSubmit, watch, setValue } = useForm()
  const [countries, setCountries] = useState<any[]>([])
  const [states, setStates] = useState<any[]>([])
  const [cities, setCities] = useState<any[]>([])
  const [isAgreementOpen, setAgreementOpen] = useState(false)

  const watchCountry = watch('country')
  const watchState = watch('state')

  useEffect(() => {
    setCountries(getCountries())
  }, [])

  useEffect(() => {
    if (watchCountry) {
      setStates(getStatesByCountry(watchCountry))
      setValue('state', '')
      setValue('city', '')
    }
  }, [watchCountry, setValue])

  useEffect(() => {
    if (watchCountry && watchState) {
      setCities(getCitiesByState(watchCountry, watchState))
      setValue('city', '')
    }
  }, [watchCountry, watchState, setValue])

  const onSubmit = (data: any) => {
    console.log('Form submitted:', data)
  }

  const formData = watch()

  return (
    <div className='h-[var(--content-height)] overflow-y-scroll'>
      <div className='max-w-4xl mx-auto my-10'>
        <StepIndicator currentStep={3} />
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          <div>
            <label htmlFor='fullName'>Full Name</label>
            <input
              {...register('fullName', { required: true })}
              type='text'
              id='fullName'
            />
          </div>

          <div>
            <label htmlFor='email'>Email</label>
            <input
              {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
              type='email'
              id='email'
            />
          </div>

          <div>
            <label htmlFor='addressLine1'>Address Line 1</label>
            <input
              {...register('addressLine1', { required: true })}
              type='text'
              id='addressLine1'
            />
          </div>

          <div>
            <label htmlFor='addressLine2'>Address Line 2</label>
            <input {...register('addressLine2')} type='text' id='addressLine2' />
          </div>

          <div>
            <label htmlFor='country'>Country</label>
            <select {...register('country', { required: true })} id='country'>
              <option value=''>Select a country</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='state'>State</label>
            <select {...register('state', { required: true })} id='state'>
              <option value=''>Select a state</option>
              {states.map((state) => (
                <option key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='city'>City</label>
            <select {...register('city', { required: true })} id='city'>
              <option value=''>Select a city</option>
              {cities.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='phoneNumber'>Phone Number</label>
            <input
              {...register('phoneNumber', { required: true })}
              type='tel'
              id='phoneNumber'
            />
          </div>

          <div>
            <SignatureCanvasComponent control={control} />
          </div>

          <div className='flex justify-between'>
            <button
              type='button'
              onClick={() => setAgreementOpen(true)}
              className='underline'
            >
              {isAgreementOpen ? 'Hide Preview' : 'Show Preview'}
            </button>
            <div className='flex items-center gap-2'>
              <button onClick={() => updateStep(1)} className='button muted'>
                Back
              </button>
              <button
                type='submit'
                className='button primary'
                onClick={() => {
                  updateStep(0)
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
        <Dialog
          open={isAgreementOpen}
          onClose={() => {
            setAgreementOpen(false)
          }}
          className='fixed inset-0 w-full h-full z-top'
        >
          <div className='fixed inset-0 md:inset-[5rem] flex items-center justify-center md:ml-72'>
            <Dialog.Panel className='absolute inset-0 bg-white shadow-lg overflow-hidden md:rounded-xl'>
              <div className='absolute inset-0'></div>
              <div className='absolute top-0 right-0 w-full flex items-center justify-end gap-1 mt-2 mr-8'>
                <button
                  className='relative z-20 border border-red-500 rounded-full bg-red-500 w-5 h-5 flex items-center justify-center text-white text-sm'
                  onClick={(e) => {
                    e.preventDefault()
                    // setImageSrc(null)
                    setAgreementOpen(false)
                  }}
                >
                  <X />
                </button>
              </div>
              <div className='relative w-full h-full p-4 overflow-x-hidden overflow-y-auto'>
                <div className='pdf-wrapper'>
                  {/* <PDFRenderer
                  type={ctx.submission.type}
                  pdfData={data}
                  // hasSignatures
                /> */}
                  agreeement preview
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  )
}

export default SignAgreement
